import { useRepositoryQuery } from '@mews-commander/data-access';
import { ActionsMenu, ChartCardActions, SidePanel } from '@mews/b2b-ui';
import { LocalizedText, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Stack } from '@optimus-web/core';
import { StatusTag } from '../../components/StatusTag';
import { SsoDetailsPanelContent } from './SsoDetailsPanelContent';
import { SsoEditConnectionDetails } from './SsoEditPanel/SsoEditConnectionDetails';
import { SsoEditToken } from './SsoEditPanel/SsoEditToken';
import { SsoEditUserDomains } from './SsoEditPanel/SsoEditUserDomains';
import { SsoStatusDialog } from './SsoStatusDialog';

export interface SsoDetailsPanelProps {
    active: boolean;
    onClose: () => void;
}

export function SsoDetailsPanel({ active, onClose, ...props }: SsoDetailsPanelProps) {
    const { data, isFetching } = useRepositoryQuery('ssoConnection', 'getOpenId', {}, { enabled: active });
    const {
        actions,
        editConnectionDialogProps,
        editUserDomainsDialogProps,
        ssoStatusDialogProps,
        editTokenDialogProps,
    } = useSsoActions(data?.isEnabled);

    return (
        <>
            <SidePanel
                opened={active}
                onClose={onClose}
                loading={isFetching}
                title={
                    <Stack spacing="150" itemAlignment="center">
                        <LocalizedText textKey={TextKey.SsoDetailsTitle} />
                        <StatusTag isEnabled={data?.isEnabled} />
                    </Stack>
                }
                content={data && <SsoDetailsPanelContent data={data} />}
                width="medium"
                headerActions={<ActionsMenu id="sso-details-actions-menu" actions={actions} />}
                {...props}
            />

            {data && (
                <>
                    <SsoEditToken data={data} {...editTokenDialogProps} />
                    <SsoEditUserDomains data={data} {...editUserDomainsDialogProps} />
                    <SsoEditConnectionDetails data={data} {...editConnectionDialogProps} />

                    <SsoStatusDialog isSsoEnabled={!!data.isEnabled} {...ssoStatusDialogProps} />
                </>
            )}
        </>
    );
}

function useSsoActions(active?: boolean) {
    const [editConnectionDialogProps, toggleEditConnectionDialog] = useDialog();
    const [editUserDomainsDialogProps, toggleEditUserDomainsDialog] = useDialog();
    const [editTokenDialogProps, toggleEditTokenDialog] = useDialog();
    const [ssoStatusDialogProps, toggleSsoStatusDialog] = useDialog();

    const actions: ChartCardActions = [
        {
            id: 'edit-configuration',
            label: <LocalizedText textKey={TextKey.SsoDetailsEditConfiguration} />,
            callback: toggleEditConnectionDialog,
        },
        {
            id: 'edit-token',
            label: <LocalizedText textKey={TextKey.SsoDetailsChangeSecret} />,
            callback: toggleEditTokenDialog,
        },
        {
            id: 'edit-user-domains',
            label: <LocalizedText textKey={TextKey.SsoDetailsManageUserDomains} />,
            callback: toggleEditUserDomainsDialog,
        },
        {
            id: 'edit-sso-status',
            label: <LocalizedText textKey={active ? TextKey.DeactivateSso : TextKey.ActivateSso} />,
            callback: toggleSsoStatusDialog,
        },
    ];

    return {
        actions,
        editConnectionDialogProps,
        editUserDomainsDialogProps,
        editTokenDialogProps,
        ssoStatusDialogProps,
    };
}
